import React from 'react';

import { formatMoney, toMoney } from '@betterment/core';
import { Table, Text } from '@betterment/samba-react';

import { BillingPlan, BillingRateFixed, BillingRateRange, PlatformFeePaidBy } from '../../../../graphql/schema';

const TieredRateTableWithNetting: React.FC<{ plan: BillingPlan; platformFee: number }> = ({ plan, platformFee }) => {
  const getTiers = (): Array<BillingRateRange> => {
    if ('tiers' in plan.billingRate) {
      return plan.billingRate.tiers;
    }
    return [];
  };
  const tiers: Array<BillingRateRange> = getTiers();

  const tierRange = (tier: BillingRateRange, index: number): string => {
    const tierStart = index === 0 ? toMoney(0) : tiers[index - 1].tierEnd;
    const tierEndText = tier.tierEnd ? `- ${formatMoney(tier.tierEnd, 'defaultOptional')}` : 'and up';

    // @ts-expect-error TS2345 violates strict null checks
    return `${formatMoney(tierStart, 'defaultOptional')} ${tierEndText}`;
  };

  const tierRate = (tier: BillingRateRange): string => {
    if (tier.firmFee.__typename === 'BillingRateFixed') {
      return `${formatMoney(tier.firmFee.feeCents, 'defaultOptional')}`;
    }
    return `${tier.firmFee.feeBps} bps`;
  };

  const tierTotalRateFixed = (fee: BillingRateFixed): string => {
    return `${formatMoney(fee.feeCents, 'defaultOptional')}`;
  };

  const platformFeeClientPortion = (): number => {
    if (plan.platformFeeAllocation.__typename === 'SharedPlatformFeeAllocation') {
      return plan.platformFeeAllocation.clientPortion;
    }
    if (plan.platformFeeAllocation.paidBy === PlatformFeePaidBy.ClientOnly) {
      return platformFee;
    }
    return 0;
  };

  const tierTotalRateWithNetting = ({ firmFee }: BillingRateRange, index: number): string => {
    const platformPortion = platformFeeClientPortion();

    if (firmFee.__typename === 'BillingRateFixed') {
      const base = tierTotalRateFixed(firmFee);
      return index === 0 ? `${base} + ${platformPortion} bps` : base;
    }

    return `${firmFee.feeBps + platformPortion} bps`;
  };

  return (
    <Table>
      <Table.Header>
        <Table.Row>
          <Table.ColumnHeaderCell
            css={{
              pl: 'space.300',
              p: 'space.200',
              verticalAlign: 'middle',
              borderRightStyle: 'solid',
              borderRightColor: 'border',
              borderRightWidth: 'thin',
            }}
          >
            Client AUM range
          </Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell
            css={{
              p: 'space.200',
              verticalAlign: 'middle',
              borderRightStyle: 'solid',
              borderRightColor: 'border',
              borderRightWidth: 'thin',
            }}
          >
            Advisory fee
          </Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell css={{ p: 'space.200', verticalAlign: 'middle' }}>
            Total paid by client
            <br />
            <Text variant="text50" color="text.weak">
              Platform fee ({platformFeeClientPortion()} bps)
            </Text>
          </Table.ColumnHeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {tiers.map((tier, index) => (
          <Table.Row key={`rateTier-${plan.id + index.toString()}`}>
            <Table.Cell
              css={{
                pl: 'space.300',
                p: 'space.200',
                verticalAlign: 'middle',
                borderRightStyle: 'solid',
                borderRightColor: 'border',
                borderRightWidth: 'thin',
                borderTopStyle: 'solid',
                borderTopColor: 'border',
                borderTopWidth: 'thin',
              }}
            >
              {tierRange(tier, index)}
            </Table.Cell>
            <Table.Cell
              css={{
                p: 'space.200',
                verticalAlign: 'middle',
                borderRightStyle: 'solid',
                borderRightColor: 'border',
                borderRightWidth: 'thin',
                borderTopStyle: 'solid',
                borderTopColor: 'border',
                borderTopWidth: 'thin',
              }}
            >
              {tierRate(tier)}
            </Table.Cell>
            <Table.Cell
              css={{
                p: 'space.200',
                verticalAlign: 'middle',
                borderTopStyle: 'solid',
                borderTopColor: 'border',
                borderTopWidth: 'thin',
              }}
            >
              {tierTotalRateWithNetting(tier, index)}
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
  );
};

export default TieredRateTableWithNetting;
